import axios from 'axios';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { API_URL } from './utils';

export function BtnTravauxTermines({ bonId, equipements, etapes, onDone }) {
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const hasEquipements = (equipements || []).length > 0;
  const allEquipTermines = !hasEquipements || (equipements || []).every(eq => eq.utilisation_terminee);
  const hasEtapes = (etapes || []).length > 0;
  const allEtapesCompletees = !hasEtapes || (etapes || []).every(e => e.completee);
  const etapesRestantes = (etapes || []).filter(e => !e.completee).length;
  const canTerminer = allEquipTermines && allEtapesCompletees;

  const handleClick = () => {
    if (!allEtapesCompletees) {
      toast.error(`${etapesRestantes} étape${etapesRestantes > 1 ? 's' : ''} non complétée${etapesRestantes > 1 ? 's' : ''}. Cochez toutes les étapes avant de continuer.`);
      return;
    }
    if (!allEquipTermines) {
      toast.error("Marquez d'abord toutes les utilisations d'équipements comme terminées.");
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setLoading(true);
    setShowConfirm(false);
    try {
      const res = await axios.post(`${API_URL}/api/crm/bons-travail/${bonId}/travaux-termines`, null);
      // 🆕 Phase E2 (2026-05-04) — `res.data` est déjà parsé par axios.
      // L'ancien `res.data.catch(() => ({}))` était un copy-paste foireux du
      // pattern fetch (`res.json().catch(...)`) → générait toast d'erreur
      // "e.data.catch is not a function" alors que l'action passait quand même.
      const data = res.data || {};
      if (res.status !== 200) throw new Error(data.detail || 'Erreur serveur');
      toast.success('Travaux marqués comme terminés ! Notifications envoyées.', { duration: 4000 });
      if (onDone) onDone();
    } catch (e) {
      toast.error(`Erreur : ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Bouton principal */}
      <Button
        onClick={handleClick}
        disabled={loading || !canTerminer}
        className={`gap-2 font-semibold shadow-md transition-all ${
          canTerminer
            ? 'bg-emerald-600 hover:bg-emerald-700 text-white hover:shadow-lg hover:scale-[1.02]'
            : 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
        }`}
        data-testid="btn-travaux-termines"
        title={
          !allEtapesCompletees ? `${etapesRestantes} étape(s) non complétée(s)`
          : !allEquipTermines ? 'Équipements en cours'
          : ''
        }
      >
        {loading
          ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi en cours...</>
          : <><CheckCircle className="w-4 h-4" /> Travaux Terminés</>
        }
      </Button>

      {/* Avertissements */}
      {hasEtapes && !allEtapesCompletees && (
        <span className="inline-flex items-center text-xs text-red-600 gap-1 no-print">
          <AlertTriangle className="w-3 h-3" />
          {etapesRestantes} étape{etapesRestantes > 1 ? 's' : ''} restante{etapesRestantes > 1 ? 's' : ''}
        </span>
      )}
      {allEtapesCompletees && hasEquipements && !allEquipTermines && (
        <span className="inline-flex items-center text-xs text-amber-600 gap-1 no-print">
          <AlertTriangle className="w-3 h-3" /> Équipements en cours
        </span>
      )}

      {/* Dialog de confirmation */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              Confirmer la fin des travaux
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <p className="text-slate-700">
              Êtes-vous certain de vouloir marquer les travaux comme <strong>terminés</strong> ?
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <p className="font-semibold mb-1">Cette action déclenchera automatiquement :</p>
              <ul className="space-y-0.5 list-disc list-inside text-amber-700">
                <li>Un courriel à la chargée de projet pour valider la satisfaction client</li>
                <li>Un courriel à l'adjoint(e) administratif(ve) pour préparer la facture finale</li>
                <li>Un courriel au propriétaire pour information</li>
              </ul>
            </div>
            <p className="text-xs text-slate-400">
              Cette action ne peut pas être annulée directement.
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowConfirm(false)} disabled={loading}>
              Annuler
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={loading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
              data-testid="btn-confirmer-travaux-termines"
            >
              {loading
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi...</>
                : <><CheckCircle className="w-4 h-4" /> Confirmer</>
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
